import React, { useEffect, useState } from 'react'
import { formatAmounts } from './../../../utilities/generalUtils'
import Skeleton from 'react-loading-skeleton'
import { useData } from '../../../data/DataProvider'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { GpCard, GpButton } from '../../../components/gp'

const dashedDivider = {
    marginTop: 18,
    height: 1,
    background:
        'repeating-linear-gradient(to right, var(--gp-hairline) 0, var(--gp-hairline) 4px, transparent 4px, transparent 8px)',
}

const statLabel = {
    font: '400 11px/1 var(--gp-font)',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: 'var(--gp-ink-mute)',
}

const statValue = { font: 'var(--gp-body)', color: 'var(--gp-ink)' }

const parseMoney = (str) => {
    const s = String(str ?? '')
    const m = s.match(/^(\D*)([\d,]+)(?:\.(\d+))?(.*)$/)
    if (!m) return { sym: '', whole: s, frac: '', suffix: '' }
    return {
        sym: m[1] || '',
        whole: m[2],
        frac: m[3] || '',
        suffix: m[4] || '',
    }
}

const Amount = ({ value, suffix }) => {
    const { sym, whole, frac } = parseMoney(value)
    return (
        <div
            style={{
                font: '500 44px/1 var(--gp-font)',
                letterSpacing: -1,
                color: 'var(--gp-ink)',
                marginTop: 6,
                display: 'flex',
                alignItems: 'baseline',
                gap: 3,
            }}
        >
            {sym && (
                <span style={{ font: '500 24px/1 var(--gp-font)', color: 'var(--gp-ink-mute)' }}>
                    {sym}
                </span>
            )}
            {whole}
            {frac && <span style={{ color: 'var(--gp-ink-mute)' }}>.{frac}</span>}
            {suffix && (
                <span style={{ font: '400 16px/1 var(--gp-font)', color: 'var(--gp-ink-mute)' }}>
                    {suffix}
                </span>
            )}
        </div>
    )
}

const MyWallet = (props) => {
    const { userWallet } = useData()
    const [walletOk, setWalletOk] = useState(true)

    useEffect(() => {
        if (
            userWallet &&
            Object.keys(userWallet).length > 0 &&
            userWallet?.paymentMethods &&
            Array.isArray(userWallet?.paymentMethods)
        ) {
            const cardOkStatus = userWallet.paymentMethods.some(
                (paymentMethod) =>
                    paymentMethod.metadata &&
                    paymentMethod.metadata.cardOk === false
            )
            setWalletOk(!cardOkStatus)
        }
    }, [userWallet])

    const defaultCard =
        Array.isArray(userWallet?.paymentMethods) &&
            userWallet.paymentMethods.length > 0
            ? userWallet.paymentMethods.find((pm) => pm.isDefault) ||
            userWallet.paymentMethods[0]
            : null
    const cardBrand = defaultCard?.card?.brand
        ? defaultCard.card.brand.charAt(0).toUpperCase() +
        defaultCard.card.brand.slice(1)
        : null
    const cardLine =
        cardBrand && defaultCard?.card?.last4
            ? `${cardBrand} · ${defaultCard.card.last4}`
            : null

    const hasBalance =
        !!(props.userWalletData && props.userWalletData?.creditBalance)
    const showAutoReload = !props.isRecurring && props.topUpAmount

    return (
        <div className="gp-col">
            <div className="dash-section-title">My Wallet</div>

            {props.loading ? (
                <GpCard padding={22}>
                    <Skeleton height="200px" borderRadius="10px" />
                </GpCard>
            ) : (
                <GpCard padding={22}>
                    {!walletOk && (
                        <div style={{ marginBottom: 14 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    color: 'var(--gp-danger)',
                                    font: 'var(--gp-body-med)',
                                }}
                            >
                                <ErrorOutlineIcon style={{ fontSize: 20 }} />
                                Fix Card Issue
                            </div>
                            <div style={dashedDivider} />
                        </div>
                    )}

                    <div
                        style={{
                            font: 'var(--gp-caption)',
                            color: 'var(--gp-ink-mute)',
                            letterSpacing: 0.3,
                        }}
                    >
                        {props.isRecurring ? 'Subscription Pricing' : 'Balance'}
                    </div>

                    {hasBalance && !props.isRecurring && (
                        <Amount value={formatAmounts(props.userWalletData?.creditBalance)} />
                    )}
                    {props.isRecurring && (
                        <Amount
                            value={formatAmounts(props.topUpAmount, false)}
                            suffix="/month"
                        />
                    )}

                    <div style={dashedDivider} />

                    <div
                        style={{
                            marginTop: 16,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                        }}
                    >
                        {showAutoReload && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={statLabel}>Auto-reload</span>
                                <span style={statValue}>
                                    {formatAmounts(props.topUpAmount, false)} at
                                    $10 balance
                                </span>
                            </div>
                        )}
                        {cardLine && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={statLabel}>Payment method</span>
                                <span style={statValue}>{cardLine}</span>
                            </div>
                        )}
                    </div>

                    <div style={{ marginTop: 22 }}>
                        <GpButton
                            variant="primary"
                            data-testid="cypress-manage-billing-button"
                            onClick={props.manageBilling}
                            style={{ width: '100%' }}
                        >
                            Manage Billing
                        </GpButton>
                    </div>
                </GpCard>
            )}
        </div>
    )
}

export default MyWallet
